import { MAIL_CLEAR_CACHE_PRESERVE_KEYS } from "@/auth/session";
import { ACCOUNT_SETTINGS_STORAGE_KEY } from "@/lib/mail/accountSettingsPrefs";
import { clearMessageLabelsRuntimeCache } from "@/hooks/useMessageLabels";
import { clearMailSnoozeRuntimeCache } from "@/hooks/useMailSnooze";
import { clearAccountsSessionCache } from "@/lib/cache/accountsSessionCache";
import {
  clearAllAttachmentFlagsCache,
  clearAttachmentFlagsCacheForAccount,
} from "@/lib/cache/attachmentFlagsCache";
import { clearAccountBootstrapDone, clearAllFolderBootstrapDone } from "@/lib/cache/folderBootstrapStore";
import { clearFoldersSessionCache, clearAllFoldersSessionCache } from "@/lib/cache/foldersSessionCache";
import { clearInboxHeadersSessionCache } from "@/lib/cache/inboxHeadersSessionCache";
import {
  clearAllMessageBodyCaches,
  clearMessageBodyCacheForAccount,
} from "@/lib/cache/messageBodyCache";
import { clearAttachmentBlobCache } from "@/lib/cache/attachmentBlobCache";
import {
  idbClearAllAttachments,
  idbClearAttachmentAccount,
} from "@/lib/cache/attachmentIndexedDB";
import { idbClearAccount } from "@/lib/cache/messageBodyIndexedDB";
import {
  idbClearAllMailStorage,
  idbClearMailStorageAccount,
} from "@/lib/storage/mailStorageIndexedDB";
import { clearOutboxQueueStore } from "@/lib/outbox/outboxQueue";
import {
  clearAccountFolderSnapshots,
  clearAllFolderSnapshots,
} from "@/lib/mail/inboxFolderBus";
import { clearAllBodiesWarming, clearAccountBodiesWarming } from "@/lib/mail/mailBodyWarmState";
import { clearMailBodyDocumentCacheForAccount } from "@/lib/message/mailBodyDocumentCache";

const MAIL_LS_PREFIXES_TO_CLEAR = ["mail_", "aurelia_mail_"] as const;

const MAIL_LS_EXACT_TO_CLEAR = [
  "mail-inbox-filter-v1",
  "mail_compose_sessions_v1",
  "mail_bodies_warmed_v2",
  "mail_inbox_session_v3",
  "mail_inbox_session_v2",
  "mail_inbox_session_v1",
  "mail_body_cache_v1",
  "mail_folders_session_v2",
  "mail_inbox_headers_session_v1",
  "mail_attachment_flags_v1",
  "mail_accounts_session_v1",
  "mail_folder_bootstrap_v1",
  "mail_folder_viewed_v1",
] as const;

function shouldPreserveLsKey(key: string): boolean {
  if ((MAIL_CLEAR_CACHE_PRESERVE_KEYS as readonly string[]).includes(key)) return true;
  if (key === "mail_build_version" || key === "mail_theme" || key === "mail_accent") return true;
  if (key === "mail_web_push_enabled_v1" || key === "mail_web_push_sw_build") return true;
  if (key === "mail_sw_prompted_build") return true;
  if (key === "mail_accounts_v1" || key === "mail_folders_session_v2") return true;
  if (key === "mail_inbox_headers_session_v1" || key === "mail_folder_bootstrap_v3") return true;
  if (key === ACCOUNT_SETTINGS_STORAGE_KEY) return true;
  return false;
}

function clearMailLocalStorage(accountId?: number): void {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || shouldPreserveLsKey(key)) continue;

      const matchPrefix = MAIL_LS_PREFIXES_TO_CLEAR.some((p) => key.startsWith(p));
      const matchExact = (MAIL_LS_EXACT_TO_CLEAR as readonly string[]).includes(key);
      if (!matchPrefix && !matchExact) continue;

      if (accountId != null) {
        const scoped =
          key.includes(String(accountId)) ||
          !key.includes(":") && !key.match(/\d/);
        if (!scoped && matchPrefix) continue;
      }

      keysToRemove.push(key);
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

export type PurgeMailClientCachesOptions = {
  accountId?: number;
  localStorage?: boolean;
  /** runtime = états volatils uniquement (warming) — sans LS ni bus inbox. */
  scope?: "full" | "runtime";
};

/**
 * Vide tous les caches client mail (mémoire, bus, LS, IndexedDB).
 * Un seul point d’entrée — SW, hub CLEAR_CACHE, modale cache.
 */
export async function purgeMailClientCaches(
  options: PurgeMailClientCachesOptions = {}
): Promise<void> {
  const { accountId, localStorage: clearLs = true, scope = "full" } = options;
  const runtimeOnly = scope === "runtime";

  if (accountId != null) {
    clearAccountFolderSnapshots(accountId);
    clearMessageBodyCacheForAccount(accountId);
    clearAttachmentFlagsCacheForAccount(accountId);
    clearMailBodyDocumentCacheForAccount(accountId);
    clearMessageLabelsRuntimeCache(accountId);
    clearMailSnoozeRuntimeCache(accountId);
    clearFoldersSessionCache(accountId);
    clearInboxHeadersSessionCache(accountId);
    clearAccountBootstrapDone(accountId);
    clearAccountBodiesWarming(accountId);
    clearAttachmentBlobCache();
    if (clearLs) {
      clearMailLocalStorage(accountId);
    }
    await idbClearAccount(accountId);
    await idbClearAttachmentAccount(accountId);
    await idbClearMailStorageAccount(accountId);
    return;
  }

  if (runtimeOnly) {
    clearAllBodiesWarming();
    clearAttachmentBlobCache();
    return;
  }

  clearAllFolderSnapshots();
  clearAllMessageBodyCaches();
  clearAllAttachmentFlagsCache();
  clearAttachmentBlobCache();
  clearMessageLabelsRuntimeCache();
  clearMailSnoozeRuntimeCache();
  clearAllBodiesWarming();
  clearAllFoldersSessionCache();
  clearAllFolderBootstrapDone();
  clearInboxHeadersSessionCache();
  if (clearLs) {
    clearMailLocalStorage();
  }

  clearAccountsSessionCache();

  await clearOutboxQueueStore();
  await idbClearAllAttachments();
  await idbClearAllMailStorage();
}
